import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    useTheme,
    IconButton,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Divider,
    
} from '@mui/material';
// removed react-virtualized usage from this file; handled in subcomponents
import { useNavigate } from 'react-router-dom';
import { publicInstance } from '../axiosInstance';
import axiosInstance from '../axiosInstance';
import { DeviceInfo as DeviceInfoType, OperatorCounts as OperatorCountsType } from './types';

import { ReportProblem as ReportProblemIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';


import DeviceDashboard from './DeviceDashboard';
// utils not needed here after refactor
import ControlPanel from './ControlPanel';
import LabelManagement from './LabelManagement';
// DeviceCard used inside DevicesGroupedList
import 'react-virtualized/styles.css';
import DevicesGroupedList from './components/DevicesGroupedList';
import SelectionWarningOverlay from './components/SelectionWarningOverlay';
import CommentDialog from './components/CommentDialog';
import { ReportIssueDialog, ConfirmReportDialog } from './components/ReportIssueDialogs';
import NotificationBar from './components/NotificationBar';

interface TimeStamp {
    time: string;
    event: string;
}

interface DeviceInfo extends DeviceInfoType {}

interface OperatorCounts extends OperatorCountsType {}

interface TestDevicesListProps {
    processedDevices: DeviceInfo[];
    serviceDevices: DeviceInfo[];
    operatorCounts: { Orange: number; Play: number; Plus: number; Unknown: number; };
    onDevicesUpdated: () => void;
    selectionMode: boolean;
    setSelectionMode: React.Dispatch<React.SetStateAction<boolean>>;
    selectedDevices: string[];
    setSelectedDevices: React.Dispatch<React.SetStateAction<string[]>>;
    allDeviceBoxes: string[];
    setAddDeviceDialogOpen: (open: boolean) => void;
    onViewHistory: () => void;
    onOpenScriptsPanel: () => void;
    filterListBySelection: boolean;
    toggleFilterListBySelection: () => void;
    onLoadCharts: () => void;
}

// Function to check if a timestamp is recent (within last 24 hours)
const isRecentTimestamp = (timestamp: string | undefined): boolean => {
    if (!timestamp) return false;
    
    try {
        const timestampDate = new Date(timestamp);
        const currentDate = new Date();
        const timeDiff = currentDate.getTime() - timestampDate.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        
        // Consider timestamps older than 1 week as not recent
        return hoursDiff <= 168; // 24 hours * 7 days = 168 hours
    } catch (error) {
        console.error('Error parsing timestamp:', error);
        return false;
    }
};


    
// (removed) CalibrationIndicators – unused

// Custom hook do opóźnionego aktualizowania wartości (debounce)
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    
    return debouncedValue;
}

const TestDevicesList: React.FC<TestDevicesListProps> = ({ 
    processedDevices, 
    serviceDevices,
    operatorCounts: initialOperatorCounts,
    onDevicesUpdated,
    selectionMode,
    setSelectionMode,
    selectedDevices,
    setSelectedDevices,
    allDeviceBoxes,
    setAddDeviceDialogOpen,
    onViewHistory,
    onOpenScriptsPanel,
    filterListBySelection,
    toggleFilterListBySelection,
    onLoadCharts
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [localDevices, setLocalDevices] = useState(processedDevices);
    const [localOperatorCounts, setLocalOperatorCounts] = useState(initialOperatorCounts);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [labelDialogOpen, setLabelDialogOpen] = useState(false);
    const [manageLabelDialogOpen, setManageLabelDialogOpen] = useState(false);
    const [commentDialogOpen, setCommentDialogOpen] = useState(false);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [comment, setComment] = useState('');
    const [issueDescription, setIssueDescription] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<string[]>(['Orange', 'Plus', 'Play', 'Unknown', 'Serwis']);
    const [filter, setFilter] = useState('');
    const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
    const [selectedModems, setSelectedModems] = useState<string[]>([]);
    const [labelFilterMode, setLabelFilterMode] = useState<'any' | 'all' | 'exact' | 'exclude'>('any');
    const [currentDevice, setCurrentDevice] = useState<DeviceInfo | null>(null);
    const [dashboardSource, setDashboardSource] = useState<'all' | 'inventory' | 'service'>('all');
    const [isAdmin, setIsAdmin] = useState(false);
    
    // Dodajemy stan dla wskaźnika ładowania podczas filtrowania
    const [isFiltering, setIsFiltering] = useState(false);
    
    // Dodajemy stan dla aktualnego filtra wpisywanego (bez opóźnienia)
    const [inputFilter, setInputFilter] = useState('');
    
    // Używamy hooka useDebounce do opóźnienia aktualizacji właściwego filtra
    const debouncedFilter = useDebounce(inputFilter, 300); // 300ms opóźnienia
    
    // Aktualizujemy właściwy filter gdy debounced wartość się zmieni
    useEffect(() => {
        if (debouncedFilter !== filter) {
            setIsFiltering(true);
            setFilter(debouncedFilter);
            
            // Symulujemy czas potrzebny na zakończenie filtrowania
            // Wystarczy około 100ms aby pokazać wskaźnik i nie opóźniać zbytnio interfejsu
            const timer = setTimeout(() => {
                setIsFiltering(false);
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [debouncedFilter]);
    
    // Dodajemy efekt dla zmiany wybranych etykiet
    useEffect(() => {
        if (selectedLabels.length > 0 || selectedModems.length > 0) {
            setIsFiltering(true);
            
            // Symulujemy czas potrzebny na zakończenie filtrowania
            const timer = setTimeout(() => {
                setIsFiltering(false);
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [selectedLabels, selectedModems]);
    
    // Removed per refactor: cache handled inside grouped list component
    
    // Removed local react-virtualized refs and pagination; handled in subcomponents
    
    useEffect(() => {
        // Check if user has admin role
        const userRole = localStorage.getItem('role');
        setIsAdmin(userRole === 'admin');
    }, []);
    
    // Derive unique labels from all devices
    const uniqueLabels = useMemo(() => {
        const labelsSet = new Set<string>();
        
        processedDevices.forEach(device => {
            if (device.label) {
                device.label.forEach(labelObj => {
                    if (labelObj.label) {
                        labelsSet.add(labelObj.label);
                    }
                });
            }
        });
        
        return Array.from(labelsSet).sort();
    }, [processedDevices]);
    
    // Derive unique modems from all devices
    const uniqueModems = useMemo(() => {
        const modemsSet = new Set<string>();
        
        processedDevices.forEach(device => {
            if (device.modem && device.modem.trim() !== '') {
                const cleanModem = device.modem.replace(/\r|\n/g, '').trim();
                modemsSet.add(cleanModem);
            }
        });
        
        return Array.from(modemsSet).sort();
    }, [processedDevices]);

    // Optymalizujemy filteredDevices do bardziej wydajnej postaci
    const filteredDevices = useMemo(() => {
        // Jeśli nie ma filtrów, zwracamy wszystkie urządzenia bez filtrowania
        if (filter === '' && selectedLabels.length === 0 && selectedModems.length === 0) {
            return processedDevices;
        }
        
        return processedDevices.filter(device => {
            // Filtrowanie po numerze box - najpierw sprawdzamy to kryterium, bo jest najszybsze
            if (filter && !device.box.toString().includes(filter)) {
                return false;
            }
            
            // Filtrowanie po etykietach - tylko jeśli są wybrane etykiety
            if (selectedLabels.length > 0) {
                if (!device.label || device.label.length === 0) {
                    // If no labels on device, exclude it for 'any', 'all', and 'exact' modes,
                    // but include it for 'exclude' mode
                    return labelFilterMode === 'exclude';
                }
                
                const deviceLabelNames = device.label.map(l => l.label);
                
                switch (labelFilterMode) {
                    case 'any':
                        // Device has ANY of the selected labels
                        if (!selectedLabels.some(selectedLabel => deviceLabelNames.includes(selectedLabel))) {
                            return false;
                        }
                        break;
                    case 'all':
                        // Device has ALL of the selected labels (can have others too)
                        if (!selectedLabels.every(selectedLabel => deviceLabelNames.includes(selectedLabel))) {
                            return false;
                        }
                        break;
                    case 'exact':
                        // Device has EXACTLY the selected labels (nothing more, nothing less)
                        if (deviceLabelNames.length !== selectedLabels.length || 
                            !selectedLabels.every(selectedLabel => deviceLabelNames.includes(selectedLabel))) {
                            return false;
                        }
                        break;
                    case 'exclude':
                        // Device has NONE of the selected labels
                        if (deviceLabelNames.some(deviceLabel => selectedLabels.includes(deviceLabel))) {
                            return false;
                        }
                        break;
                }
            }
            
            // Filtrowanie po modemach - tylko jeśli są wybrane modemy
            if (selectedModems.length > 0) {
                if (!device.modem || device.modem.trim() === '') {
                    return false;
                }
                
                const cleanModem = device.modem.replace(/\r|\n/g, '').trim();
                if (!selectedModems.includes(cleanModem)) {
                    return false;
                }
            }
            
            return true;
        });
    }, [processedDevices, filter, selectedLabels, selectedModems, labelFilterMode]);

    // Podobnie optymalizujemy filteredServiceDevices 
    const filteredServiceDevices = useMemo(() => {
        if (!serviceDevices) return [];
        
        // Jeśli nie ma filtrów, zwracamy wszystkie urządzenia bez filtrowania
        if (filter === '' && selectedLabels.length === 0 && selectedModems.length === 0) {
            return serviceDevices;
        }
        
        return serviceDevices.filter(device => {
            // Filtrowanie po numerze box - najpierw sprawdzamy to kryterium, bo jest najszybsze
            if (filter && !device.box.toString().includes(filter)) {
                return false;
            }
            
            // Filtrowanie po etykietach - tylko jeśli są wybrane etykiety
            if (selectedLabels.length > 0) {
                if (!device.label || device.label.length === 0) {
                    // If no labels on device, exclude it for 'any', 'all', and 'exact' modes,
                    // but include it for 'exclude' mode
                    return labelFilterMode === 'exclude';
                }
                
                const deviceLabelNames = device.label.map(l => l.label);
                
                switch (labelFilterMode) {
                    case 'any':
                        // Device has ANY of the selected labels
                        if (!selectedLabels.some(selectedLabel => deviceLabelNames.includes(selectedLabel))) {
                            return false;
                        }
                        break;
                    case 'all':
                        // Device has ALL of the selected labels (can have others too)
                        if (!selectedLabels.every(selectedLabel => deviceLabelNames.includes(selectedLabel))) {
                            return false;
                        }
                        break;
                    case 'exact':
                        // Device has EXACTLY the selected labels (nothing more, nothing less)
                        if (deviceLabelNames.length !== selectedLabels.length || 
                            !selectedLabels.every(selectedLabel => deviceLabelNames.includes(selectedLabel))) {
                            return false;
                        }
                        break;
                    case 'exclude':
                        // Device has NONE of the selected labels
                        if (deviceLabelNames.some(deviceLabel => selectedLabels.includes(deviceLabel))) {
                            return false;
                        }
                        break;
                }
            }
            
            // Filtrowanie po modemach - tylko jeśli są wybrane modemy
            if (selectedModems.length > 0) {
                if (!device.modem || device.modem.trim() === '') {
                    return false;
                }
                
                const cleanModem = device.modem.replace(/\r|\n/g, '').trim();
                if (!selectedModems.includes(cleanModem)) {
                    return false;
                }
            }
            
            return true;
        });
    }, [serviceDevices, filter, selectedLabels, selectedModems, labelFilterMode]);

    // Optymalizujemy tworzenie dashboardDevices, aby było bardziej wydajne
    const dashboardDevices = useMemo(() => {
        // Używamy switch, aby unikać niepotrzebnych filtracji i łączeń
        switch (dashboardSource) {
            case 'inventory':
                return filteredDevices;
            case 'service':
                return filteredServiceDevices;
            case 'all':
            default:
                // Tylko jeśli mamy oba źródła aktywne, łączymy tablice
                if (filteredDevices.length === 0) return filteredServiceDevices;
                if (filteredServiceDevices.length === 0) return filteredDevices;
                return [...filteredDevices, ...filteredServiceDevices];
        }
    }, [dashboardSource, filteredDevices, filteredServiceDevices]);

    // Calculate operator counts for the dashboard devices
    const dashboardOperatorCounts = useMemo(() => {
        return dashboardDevices.reduce((acc, device) => {
            const operator = device.operator || 'Unknown';
            if (operator === 'Orange') acc.Orange++;
            else if (operator === 'Play') acc.Play++;
            else if (operator === 'Plus') acc.Plus++;
            else acc.Unknown++;
            return acc;
        }, { Orange: 0, Play: 0, Plus: 0, Unknown: 0 });
    }, [dashboardDevices]);

    // Dashboard metrics
    const dashboardMetrics = useMemo(() => {
        const totalDashboardDevices = dashboardDevices.length;
        const devicesWithTimestamp = dashboardDevices.filter(d => !!d.time_stamp).length;
        const recentDashboardDevices = dashboardDevices.filter(d => isRecentTimestamp(d.time_stamp)).length;
        const outdatedDashboardDevices = devicesWithTimestamp - recentDashboardDevices;
        const dashboardDevicesWithoutTimestamp = totalDashboardDevices - devicesWithTimestamp;

        return {
            totalDevices: totalDashboardDevices,
            recentDevices: recentDashboardDevices,
            outdatedDevices: outdatedDashboardDevices,
            devicesWithoutTimestamp: dashboardDevicesWithoutTimestamp
        };
    }, [dashboardDevices]);

    // Update operator counts based on filtered devices
    useEffect(() => {
        // Recalculate operator counts based on filtered devices
        const counts = filteredDevices.reduce((acc, device) => {
            const operator = device.operator || 'Unknown';
            if (operator === 'Orange') acc.Orange++;
            else if (operator === 'Play') acc.Play++;
            else if (operator === 'Plus') acc.Plus++;
            else acc.Unknown++;
            return acc;
        }, { Orange: 0, Play: 0, Plus: 0, Unknown: 0 });
        
        setLocalOperatorCounts(counts);
        
        // Log for debugging
        console.log('Filtered devices count:', filteredDevices.length);
        console.log('Updated operator counts:', counts);
    }, [filteredDevices]);

    // Add these calculations
    const totalDevices = processedDevices.length;
    const devicesWithTimestamp = processedDevices.filter(d => !!d.time_stamp).length;
    const recentDevices = processedDevices.filter(d => isRecentTimestamp(d.time_stamp)).length;
    const outdatedDevices = devicesWithTimestamp - recentDevices;
    const devicesWithoutTimestamp = totalDevices - devicesWithTimestamp;

    // Update the groupedDevices logic to include label filtering
    const groupedDevices = useMemo(() => {
        const groups: { [key: string]: DeviceInfo[] } = {};
        
        processedDevices.forEach(device => {
            // Apply box number filter
            const matchesBoxFilter = !filter || device.box.includes(filter);
            
            // Apply label filter based on selected mode
            let matchesLabelFilter = true;
            if (selectedLabels.length > 0) {
                if (!device.label || device.label.length === 0) {
                    // If no labels on device, exclude it for 'any', 'all', and 'exact' modes,
                    // but include it for 'exclude' mode
                    matchesLabelFilter = labelFilterMode === 'exclude';
                } else {
                    const deviceLabelNames = device.label.map(l => l.label);
                    
                    switch (labelFilterMode) {
                        case 'any':
                            // Device has ANY of the selected labels
                            matchesLabelFilter = selectedLabels.some(selectedLabel => 
                                deviceLabelNames.includes(selectedLabel)
                            );
                            break;
                        case 'all':
                            // Device has ALL of the selected labels (can have others too)
                            matchesLabelFilter = selectedLabels.every(selectedLabel => 
                                deviceLabelNames.includes(selectedLabel)
                            );
                            break;
                        case 'exact':
                            // Device has EXACTLY the selected labels (nothing more, nothing less)
                            matchesLabelFilter = deviceLabelNames.length === selectedLabels.length && 
                                selectedLabels.every(selectedLabel => deviceLabelNames.includes(selectedLabel));
                            break;
                        case 'exclude':
                            // Device has NONE of the selected labels
                            matchesLabelFilter = !deviceLabelNames.some(deviceLabel => 
                                selectedLabels.includes(deviceLabel)
                            );
                            break;
                    }
                }
            }
                 
            // Apply modem filter - device matches if it has the selected modem
            const matchesModemFilter = selectedModems.length === 0 ||
                (device.modem && device.modem.trim() !== '' &&
                 selectedModems.includes(device.modem.replace(/\r|\n/g, '').trim()));

            if (!matchesBoxFilter || !matchesLabelFilter || !matchesModemFilter) {
                return;
            }
            
            // Determine the primary group (operator or first label)
            let primaryGroup = device.operator || 'Unknown';
            
            // Initialize the group if it doesn't exist
            if (!groups[primaryGroup]) {
                groups[primaryGroup] = [];
            }
            
            // Add the device to its group
            groups[primaryGroup].push(device);
        });
        
        // Sort devices within each group
        Object.values(groups).forEach(devicesGroup => {
            devicesGroup.sort((a, b) => parseInt(a.box) - parseInt(b.box));
        });
        
        return groups;
    }, [processedDevices, filter, selectedLabels, selectedModems, labelFilterMode]);
    
    // Organize secondary groups within each primary group
    const organizedGroups = useMemo(() => {
        const result: { 
            [primaryGroup: string]: {
                devices: DeviceInfo[],
                subgroups: { [secondaryGroup: string]: DeviceInfo[] }
            }
        } = {};
        
        // Initialize all primary groups
        Object.keys(groupedDevices).forEach(primaryGroup => {
            result[primaryGroup] = {
                devices: [],
                subgroups: {}
            };
            
            // Categorize devices into subgroups or main devices
            groupedDevices[primaryGroup].forEach(device => {
                if (device.secondaryGroup) {
                    // Add to appropriate subgroup
                    if (!result[primaryGroup].subgroups[device.secondaryGroup]) {
                        result[primaryGroup].subgroups[device.secondaryGroup] = [];
                    }
                    result[primaryGroup].subgroups[device.secondaryGroup].push(device);
                } else {
                    // Add to main devices list if no secondary group
                    result[primaryGroup].devices.push(device);
                }
            });
        });
        
        return result;
    }, [groupedDevices]);
    
    // Now we can calculate these variables after the useMemo hooks
    const effectiveExpandedGroups = filter ? 
        Object.keys(groupedDevices || {}) : 
        expandedGroups;

    // Zmień sposób obsługi zmiany źródła danych na memoizowany callback
    const handleSourceChange = useCallback((source: 'all' | 'inventory' | 'service') => {
        setDashboardSource(source);
    }, []);
    
    // Dodaj callback do obsługi filtrowania
    const handleFilterChange = useCallback((value: string) => {
        setInputFilter(value);
    }, []);
    
    // Dodaj callback do obsługi wyboru etykiet
    const handleLabelsChange = useCallback((labels: string[]) => {
        setSelectedLabels(labels);
    }, []);
    
    // Memoizuj callback dla wyboru urządzenia
    const handleDeviceSelection = useCallback((box: string) => {
        if (!selectionMode) return;
        
        setSelectedDevices(prev => 
            prev.includes(box)
                ? prev.filter(b => b !== box)
                : [...prev, box]
        );
    }, [selectionMode, setSelectedDevices]);
    
    // Przenosimy useCallback dla getDeviceSelectionHandler na poziom głównego komponentu
    const getDeviceSelectionHandler = useCallback((box: string) => {
        return () => handleDeviceSelection(box);
    }, [handleDeviceSelection]);

    const handleToggleSelection = useCallback(() => {
        // Only admins can toggle selection mode
        if (!isAdmin) {
            setSnackbarMessage('You need admin privileges to use selection mode');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // Resetujemy zaznaczenie PRZED zmianą stanu, aby uniknąć niepotrzebnych re-renderów
        if (selectionMode) {
            setSelectedDevices([]);
        }
        
        setSelectionMode(!selectionMode);
    }, [isAdmin, selectionMode, setSelectedDevices, setSelectionMode, setSnackbarMessage, setSnackbarOpen, setSnackbarSeverity]);
    
    // Dodajemy funkcję toggleGroup jako useCallback przed jej użyciem
    const toggleGroup = useCallback((group: string) => {
        setExpandedGroups(prev => 
            prev.includes(group) 
                ? prev.filter(g => g !== group) 
                : [...prev, group]
        );
    }, []);
    
    // Virtualized list logic moved into DevicesGroupedList
    
    // renderDeviceGroups moved to DevicesGroupedList

    const handleRemoveDevices = useCallback((box: string) => {
        try {
            // Remove the device from the localDevices state
            setLocalDevices(prevDevices => prevDevices.filter(device => device.box !== box));

            // Update the localOperatorCounts
            setLocalOperatorCounts(prevCounts => ({
                ...prevCounts,
                Orange: prevCounts.Orange,
                Play: prevCounts.Play,
                Plus: prevCounts.Plus,
                Unknown: prevCounts.Unknown
            }));

            // Update the localOperatorCounts based on the filtered devices
            const updatedCounts = filteredDevices.reduce((acc, device) => {
                const operator = device.operator || 'Unknown';
                if (operator === 'Orange') acc.Orange--;
                else if (operator === 'Play') acc.Play--;
                else if (operator === 'Plus') acc.Plus--;
                else acc.Unknown--;
                return acc;
            }, { Orange: 0, Play: 0, Plus: 0, Unknown: 0 });
            
            setLocalOperatorCounts(updatedCounts);

            // Log for debugging
            console.log('Removed device from localDevices');
            console.log('Updated localOperatorCounts:', updatedCounts);

            onDevicesUpdated();
        } catch (error) {
            console.error('Error removing device:', error);
            setSnackbarMessage('Failed to remove device from inventory');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    }, [filteredDevices, onDevicesUpdated]);

    // Add a wrapper function for the ControlPanel that matches the expected signature
    const handleRemoveSelectedDevices = () => {
        if (selectedDevices.length === 0) {
            setSnackbarMessage('No devices selected to remove');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // Now handle the removal of all selected devices
        publicInstance.patch(
            `/dacapi_sensorbox_introduction_form?box=in.(${selectedDevices.join(',')})`,
            {
                status: 'SPRAWNY'
            }
        )
        .then(response => {
            if (response.status === 204) {
                // Show success message
                setSnackbarMessage(`Successfully removed ${selectedDevices.length} device(s) from inventory`);
                setSnackbarSeverity('success');
                setSnackbarOpen(true);

                // Clear selection and update devices
                setSelectedDevices([]);
                setSelectionMode(false);
                onDevicesUpdated();
            } else {
                throw new Error('Failed to update device status');
            }
        })
        .catch(error => {
            console.error('Error removing devices:', error);
            setSnackbarMessage('Failed to remove devices from inventory');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        });
    };

    const handleAddLabels = () => {
        if (selectedDevices.length > 0) {
            setCurrentDevice(null); // Reset current device since we're dealing with multiple
            setLabelDialogOpen(true);
        } else {
            setSnackbarMessage('Please select at least one device first');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleManageLabels = () => {
        setManageLabelDialogOpen(true);
    };
    
    const handleOpenCommentDialog = () => {
        if (selectedDevices.length > 0) {
            setCommentDialogOpen(true);
        } else {
            setSnackbarMessage('Please select at least one device first');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleAddComment = async () => {
        // Allow empty comments for removal, but still require device selection
        if (selectedDevices.length === 0) return;

        try {
            // Group devices by their type (service or regular)
            const serviceBoxes: string[] = [];
            const regularBoxes: string[] = [];
            
            // Check if each selected device is a service device or regular device
            selectedDevices.forEach(box => {
                const isServiceDevice = serviceDevices?.some(device => device.box === box) || false;
                if (isServiceDevice) {
                    serviceBoxes.push(box);
                } else {
                    regularBoxes.push(box);
                }
            });

            // Process regular devices - use dacapi_inventory_current
            for (const box of regularBoxes) {
                await publicInstance.post('/dacapi_inventory_current', {
                    box: parseInt(box),
                    comments: comment.trim()
                }, {
                    headers: {
                        'Prefer': 'resolution=merge-duplicates'
                    }
                });
            }

            // Process service devices - use dacapi_inventory_temp
            for (const box of serviceBoxes) {
                await publicInstance.post('/dacapi_inventory_temp', {
                    box: parseInt(box),
                    comments: comment.trim()
                }, {
                    headers: {
                        'Prefer': 'resolution=merge-duplicates'
                    }
                });
            }

            if (comment.trim() === '') {
                setSnackbarMessage(`Successfully removed comments from ${selectedDevices.length} device(s)`);
            } else {
                setSnackbarMessage(`Successfully added comment to ${selectedDevices.length} device(s)`);
            }
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setCommentDialogOpen(false);
            setComment('');
            onDevicesUpdated();
        } catch (error) {
            console.error('Error processing comments:', error);
            setSnackbarMessage('Failed to process comments');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Add a new state for confirmation dialog
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    // Add state for fullscreen warning
    const [showFullscreenWarning, setShowFullscreenWarning] = useState(false);

    const handleReportIssue = () => {
        if (selectedDevices.length > 0) {
            setReportDialogOpen(true);
        } else {
            setSnackbarMessage('Please select at least one device first');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleIssueSubmitAttempt = () => {
        // Check if description is provided
        if (!issueDescription.trim()) {
            setSnackbarMessage('Please enter an issue description');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // Close the report dialog
        setReportDialogOpen(false);
        
        // For multiple devices, show dramatic warning
        if (selectedDevices.length > 1) {
            setShowFullscreenWarning(true);
            // Auto-hide after 3 seconds and show confirmation dialog
            setTimeout(() => {
                setShowFullscreenWarning(false);
                setConfirmDialogOpen(true);
            }, 3000);
        } else {
            // For single device, just show normal confirmation
            setConfirmDialogOpen(true);
        }
    };

    const handleIssueSubmit = async () => {
        if (!issueDescription.trim() || selectedDevices.length === 0) return;

        try {
            // Report the issue for each selected device
            for (const box of selectedDevices) {
                await axiosInstance.post('/servicepanel/report_issue/', {
                    box: box,
                    comment: issueDescription.trim(),
                }, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
            }

            setSnackbarMessage(`Successfully reported issue for ${selectedDevices.length} device(s)`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setReportDialogOpen(false);
            setConfirmDialogOpen(false);
            setShowFullscreenWarning(false);
            setIssueDescription('');
            
            // Clear selection and refresh devices
            setSelectedDevices([]);
            setSelectionMode(false);
            onDevicesUpdated();
        } catch (error) {
            console.error('Error reporting issue:', error);
            setSnackbarMessage('Failed to report issue');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Function to handle view history - now redirects to the history page
    const handleViewHistory = async () => {
        if (onViewHistory) {
            onViewHistory();
            return;
        }
        
        // If no prop provided, navigate directly
        navigate('/inventory/history');
    };

    // Fetch chart data for all devices
    /*
    useEffect(() => {
        const fetchChartData = async () => {
            if (!processedDevices.length) return;
            
            const deviceNumbers = processedDevices.map(d => d.device_number);
            const promises = deviceNumbers.map(async (deviceNumber) => {
                try {
                    const response = await publicInstance.get(`/dacapi_inventory_chart_data/${deviceNumber}`);
                    return {
                        deviceNumber,
                        data: response.data
                    };
                } catch (error) {
                    console.error(`Error fetching chart data for device ${deviceNumber}:`, error);
                    return null;
                }
            });

            const results = await Promise.all(promises);
            const validResults = results.filter((result): result is { deviceNumber: string; data: any } => result !== null);
            
            setChartData(prev => {
                const newData = { ...prev };
                validResults.forEach(({ deviceNumber, data }) => {
                    newData[deviceNumber] = data;
                });
                return newData;
            });
        };

        fetchChartData();
    }, [processedDevices]);
    */

    // renderServiceDevices moved to DevicesGroupedList

    return (
        <Box sx={{ 
            display: 'flex', 
            position: 'relative',
            flexDirection: 'row-reverse' // Reverse the order to make the right panel appear first in DOM
        }}>
            {/* Control Panel */}
            <Box sx={{ width: '240px', flexShrink: 0, display: { xs: 'none', md: 'block' }, position: 'relative' }}>
                <ControlPanel
                    filter={inputFilter}
                    setFilter={setInputFilter}
                    selectedLabels={selectedLabels}
                    setSelectedLabels={setSelectedLabels}
                    uniqueLabels={uniqueLabels}
                    selectedModems={selectedModems}
                    setSelectedModems={setSelectedModems}
                    uniqueModems={uniqueModems}
                    selectionMode={selectionMode}
                    handleToggleSelection={handleToggleSelection}
                    selectedDevices={selectedDevices}
                    setSelectedDevices={setSelectedDevices}
                    setSelectionMode={setSelectionMode}
                    allDeviceBoxes={allDeviceBoxes}
                    setAddDeviceDialogOpen={setAddDeviceDialogOpen}
                    totalDevices={totalDevices}
                    onRemoveDevices={handleRemoveSelectedDevices}
                    onManageLabels={handleManageLabels}
                    onAddLabels={handleAddLabels}
                    onAddComment={handleOpenCommentDialog}
                    onReportIssue={handleReportIssue}
                    onViewHistory={onViewHistory}
                    onOpenScriptsPanel={onOpenScriptsPanel}
                    onLoadCharts={onLoadCharts}
                    isFiltering={isFiltering}
                    labelFilterMode={labelFilterMode as 'any' | 'all' | 'exact' | 'exclude'}
                    setLabelFilterMode={setLabelFilterMode}
                    filterListBySelection={filterListBySelection}
                    toggleFilterListBySelection={toggleFilterListBySelection}
                />
            </Box>
            
            {/* Main content area */}
            <Box sx={{ flexGrow: 1, width: 'calc(100% - 240px)' }}>
                <DeviceDashboard 
                    processedDevices={dashboardDevices} 
                    operatorCounts={dashboardOperatorCounts}
                    totalDevices={dashboardMetrics.totalDevices}
                    recentDevices={dashboardMetrics.recentDevices}
                    outdatedDevices={dashboardMetrics.outdatedDevices}
                    devicesWithoutTimestamp={dashboardMetrics.devicesWithoutTimestamp}
                    theme={theme}
                    sourceType={dashboardSource}
                    onSourceChange={handleSourceChange}
                />
                
                {/* Wskaźnik ładowania podczas filtrowania */}
                {isFiltering && (
                    <Box 
                        sx={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            padding: 2,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                            backdropFilter: 'blur(4px)',
                            boxShadow: theme.shadows[4],
                            zIndex: 1000
                        }}
                    >
                        <CircularProgress size={40} color="primary" />
                        <Typography variant="body2" color="text.secondary">
                            Filtrowanie urządzeń...
                        </Typography>
                    </Box>
                )}
                
                <Box>
                    {(dashboardSource === 'all' || dashboardSource === 'inventory' || dashboardSource === 'service') && (
                        (filteredDevices.length > 0 || filteredServiceDevices.length > 0) ? (
                            <DevicesGroupedList
                                devices={dashboardSource === 'all' ? [...filteredDevices, ...filteredServiceDevices] : 
                                       dashboardSource === 'inventory' ? filteredDevices : 
                                       filteredServiceDevices}
                                filter={filter}
                                selectedLabels={selectedLabels}
                                selectedModems={selectedModems}
                                labelFilterMode={labelFilterMode}
                                expandedGroups={effectiveExpandedGroups}
                                onToggleGroup={toggleGroup}
                                selectionMode={selectionMode}
                                selectedDevices={selectedDevices}
                                onGetSelectionHandler={getDeviceSelectionHandler}
                                onDevicesUpdated={onDevicesUpdated}
                            />
                        ) : (
                            <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
                                No devices found.
                            </Typography>
                        )
                    )}
                </Box>
            </Box>

            <LabelManagement
                open={labelDialogOpen}
                onClose={() => setLabelDialogOpen(false)}
                selectedDevices={selectedDevices}
                serviceDevices={serviceDevices ? serviceDevices.map(device => device.box) : []}
                onLabelsAssigned={onDevicesUpdated}
                mode="assign"
            />

            <LabelManagement
                open={manageLabelDialogOpen}
                onClose={() => setManageLabelDialogOpen(false)}
                selectedDevices={[]}
                serviceDevices={[]}
                onLabelsAssigned={onDevicesUpdated}
                mode="manage"
            />

            <CommentDialog
                open={commentDialogOpen}
                onClose={() => setCommentDialogOpen(false)}
                comment={comment}
                onChange={setComment}
                onSubmit={handleAddComment}
            />

            <ReportIssueDialog
                open={reportDialogOpen}
                onClose={() => setReportDialogOpen(false)}
                issueDescription={issueDescription}
                onChange={setIssueDescription}
                onSubmitAttempt={handleIssueSubmitAttempt}
                selectedCount={selectedDevices.length}
            />

            <ConfirmReportDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleIssueSubmit}
                selectedCount={selectedDevices.length}
            />

            <NotificationBar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />
            
            <SelectionWarningOverlay open={showFullscreenWarning} count={selectedDevices.length} />
        </Box>
    );
};

// Add this right before the export default line
export default TestDevicesList;